import moment from 'moment';

/**
 * @param {string} action action being taken
 * @param {string} mode 
 * @returns {string} string representing the constant for the provided action
 */
function getActionConstant(action, mode) {
  return `${mode}.${action}`;
}

/**
 * @param {object} q the query object 
 * @returns {boolean} represents whether the provided query is just time based or not
 */
function isSimpleTimeQuery(q) {
  let isSimple = true;
  if (q.group && q.group.length > 0) {
    isSimple = false;
  }
  if (q.order && q.order.length > 1) {
    isSimple = false;
  }
  if (q.order && q.order[0]) {
    if (typeof q.order[0] === 'string' && q.order[0] !== '$time' && q.order[0] !== 'time') {
      isSimple = false;
    } else if (Array.isArray(q.order[0]) && q.order[0][0] !== '$time' && q.order[0][0] !== 'time') {
      isSimple = false;
    }
  }
  if (q.paths) {
    q.paths.forEach(path => {
      if (Array.isArray(path) && path[2] != null) {
        isSimple = false;
      }
    });
  }
  return isSimple;
}

/**
 * @param {object} opts the query object 
 * @returns {boolean} represents whether the provided query is in descending order or not
 */
function isReversed(opts) {
  const nOpts = getQueryOptions(opts); // getQueryOptions( opts )
  const {
    order
  } = nOpts;
  if (Array.isArray(order[0]) && order[0][1] === 'DESC') {
    return true;
  }
  return false;
}

/**
 * @param {array} items current items
 * @param {array} newItems items to be merged in
 * @param {array} range tuple of start and end
 * @returns {array} array of items with newItems inserted in for the given range
 */
function mergeItems(items, newItems, range) {
  const [start, end] = range;
  let before = items.slice(0, start);
  const during = newItems.slice(0, end - start);
  const after = items.slice(end);
  if (before.length !== start) {
    const temp = new Array(start - before.length);
    before = [...before, ...temp];
  }
  return [...before, ...during, ...after];
}
function sortByStartIndex(a, b) {
  return a[0] - b[0];
}

/**
 * @param {array} ranges 
 * @returns {array} array where all the ranges that overlapped are joined
 */
/*
  cases : 
    a and b touch/overlap
    a and b are disparate
*/
function mergeRanges(ranges = []) {
  if (ranges.length === 0) {
    return ranges;
  }
  const nRanges = ranges.map(range => [...range]).sort(sortByStartIndex);
  const mergedRanges = [];
  let currentRange = nRanges[0];
  nRanges.slice(1).forEach((range, index) => {
    // range[0] >= currentRange[0] always

    // overlap or touch
    if (range[0] <= currentRange[1]) {
      currentRange = [currentRange[0], Math.max(range[1], currentRange[1])];
      // separate
    } else {
      mergedRanges.push(currentRange);
      currentRange = range;
    }

    // we're done always push the last one
    // if ( index === nRanges.length - 2 ) {
    //   mergedRanges.push( currentRange )
    // }
  });
  mergedRanges.push(currentRange);
  return mergedRanges;
}

/**
 * @param {object} opts 
 * @param {number} chunkSize size of the api result queries
 * @returns {object} options for an api call for a chunk of data
 */
function getQueryChunks(opts, chunkSize) {
  const nOpts = getQueryOptions(opts); // getQueryOptions( opts )
  const {
    start,
    end = Date.now(),
    order
  } = nOpts;
  let reversed = false;
  if (Array.isArray(order[0]) && order[0][1] === 'DESC') {
    reversed = true;
  }
  const nStart = moment.utc(start),
    nEnd = moment.utc(end);
  const times = [nStart];

  // align chunks by day as much as possible
  const alignStart = nStart.clone().startOf('day').valueOf();
  const n = Math.ceil((nStart.valueOf() - alignStart) / chunkSize);
  let toPush = moment(alignStart + n * chunkSize);
  while (toPush.valueOf() < nEnd.valueOf()) {
    times.push(toPush);
    toPush = toPush.clone().add(chunkSize, 'ms');
  }
  times.push(nEnd);
  const toReturn = [];
  for (let i = 0; i < times.length - 1; i++) {
    toReturn.push({
      ...nOpts,
      start: times[i].valueOf(),
      end: times[i + 1].valueOf() - 1
    });
  }
  return reversed ? toReturn.reverse() : toReturn;
}

/**
 * @param {object} opts query options
 * @returns {object} returns the opts object with correct start and end
 */
function getQueryOptions(opts) {
  const nOpts = {
    ...opts
  };
  if (typeof nOpts.getStart === 'function') {
    nOpts.start = nOpts.getStart(nOpts);
    delete nOpts.getStart;
  }
  if (typeof nOpts.getEnd === 'function') {
    nOpts.end = nOpts.getEnd(nOpts);
    delete nOpts.getEnd;
  }
  if (nOpts.name) {
    delete nOpts.name;
  }
  return nOpts;
}
function removeDollar(path) {
  if (path[0] === '$') {
    return path.slice(1);
  }
  return path;
}
function isRoot(path) {
  const nPath = typeof path === 'string' ? path : path[0];
  return nPath.startsWith('$') && !nPath.includes('/');
}
export { isSimpleTimeQuery, getActionConstant, mergeRanges, mergeItems, getQueryOptions, getQueryChunks, removeDollar, isRoot, isReversed };